import useSWRImmutable from 'swr/immutable';

import { ShipmentVehicleModel } from '@/lib/models/shipment/types';
import { MapMarker, Paths } from '@/types';
import { AsyncDataInterface } from '@/types/context-types';
import { request } from '@/utils/api/request';
import { getVesselLocationsUrl } from '@/utils/helpers/api-urls';
import { logEvent } from '@/utils/logger';

interface VesselLocationsState extends AsyncDataInterface {
  markers: MapMarker[];
  paths: Paths[];
}

export interface VesselLocationsApiResponse {
  markers: MapMarker[];
  paths: Paths[];
}

interface UseVesselLocationsProps {
  vehicles: ShipmentVehicleModel[];
}

const ERROR_MESSAGE = 'Error fetching vessel location data';

export const useVesselLocations = ({ vehicles }: UseVesselLocationsProps): VesselLocationsState => {
  const getRequestBody = () => ({
    body: { vehicles },
  });

  const { data, isLoading, error } = useSWRImmutable<VesselLocationsApiResponse[]>(
    getVesselLocationsUrl(vehicles),
    (url: string) => request(url, getRequestBody()),
    {
      onError: (err) => {
        logEvent.error('useVesselLocations request error', { err });
      },
      errorRetryCount: 2,
    },
  );

  const errorMessage = error ? ERROR_MESSAGE : undefined;

  const flattenedData = data
    ? {
        markers: data.flatMap((row) => row.markers),
        paths: data.flatMap((row) => row.paths),
      }
    : undefined;

  return { ...(flattenedData || { markers: [], paths: [] }), isLoading, error: errorMessage };
};
